import React, {useContext} from "react";
import {ISearchResult} from "@jorsek/content-portal-client/types"
import {createStyles, withStyles} from "@material-ui/core";
import Pagination from "material-ui-flat-pagination";
import PortalBreadcrumb from "./PortalBreadcrumb";
import I18nMessage from "./i18n/I18nMessage";
import PortalLink from "./PortalLink";
import RenderHtml from "./RenderHtml";
import {getLocalizedTaxonomyLabel} from "../utils/TaxonomyLabelUtil";
import {I18nContext} from "../i18n/index";
import {DeprecatedBadge} from "@jorsek/elements-core";
import {multiReplaceAmp} from "utils/string";


const searchResultItemStyles = createStyles((theme: any) => ({
    searchResultMetadata: theme.searchResultMetadata,
    searchResultMetadataItem: theme.searchResultMetadataItem,
    searchResultSubTitle: theme.searchResultSubTitle,
    searchResultItemHref: theme.searchResultItemHref,
    searchResultItemHighlights: theme.searchResultItemHighlights,
    searchResultItem: theme.searchResultItem,
    searchResultBreadcrumbs: theme.searchResultBreadcrumbs,
    "@global": {
        ".ezd-portal_search-index_results .elementsCoreContainer .sl-text-heading": {
            display: "none"
        },
        ".ezd-portal_search-index_results .elementsCoreContainer .sl-text-lg": {
            "min-width": "50px",
            "text-align": "center",
            "font-size": "12px",
            "padding-left": "0",
            "padding-right": "0"
        },
        ".ezd-portal_search-index_results .elementsCoreContainer .sl-stack .sl-relative": {
            marginTop: "0"
        }
    }
}));

interface IOpenApiPageInfo {
    method: string;
    path: string;
    deprecated: boolean;
}

interface IRowProps extends ISearchResult {
    classes?: any,
    customMetadata: any;
    contentHref?: string;
    openApiPageInfo?: IOpenApiPageInfo
    explanation?: string
}

const SearchResultItem = (row: IRowProps) => {
    const i18nContext = useContext(I18nContext);
    const {classes} = row
    const taxonomyValues = Object.values(row.customMetadata.taxonomy);
    return (
        <div className={classes.searchResultItem}>
            <PortalLink className={classes.searchResultItemHref}
                        shallow={true}
                        scroll={false}
                        linkProps={{variant: "h5", color: "textPrimary"}}
                        href={row.contentHref || row.href}>
                {row.title}
                {row.openApiPageInfo && row.openApiPageInfo.deprecated &&
                    <div className="elementsCoreWrapper" style={{display: 'inline-block', marginLeft: '5px'}}>
                        <div className="elementsCoreContainer" style={{display: 'inline-block', pointerEvents: 'none'}}>
                            <DeprecatedBadge/>
                        </div>
                    </div>
                }
                {row.openApiPageInfo &&
                <portal-operation-header id={row.href} 
                                 method={row.openApiPageInfo.method}
                                 path={row.openApiPageInfo.path}/>
                }
            </PortalLink>
            <span className={classes.searchResultSubTitle}>{row.shortDescription}</span>
            <div className={classes.searchResultItemHighlights}>
                {row.highlights.map((obj, index) => (<RenderHtml key={index}>{obj}</RenderHtml>))}
            </div>
            {row.explanation ? (
                <textarea>
                { row.explanation }
            </textarea>
            ) : null }
            <div className={classes.searchResultBreadcrumbs}>
                <PortalBreadcrumb
                    shallow={true}
                    breadcrumbs={row.breadcrumbs}
                    scrollNavLink={false}
                    />
            </div>
            {taxonomyValues.length ? (
                <div className={classes.searchResultMetadata}>
                    {taxonomyValues.map(({values}) => values.flat().map((value) => {
                            const label = getLocalizedTaxonomyLabel(i18nContext, value.value, value.humanReadable);
                            return (
                                <div key={value.humanReadable}
                                     className={classes.searchResultMetadataItem}>
                                    {label}
                                </div>
                            )
                        }
                    ))}
                </div>
            ) : null}
        </div>
    );
};

SearchResultItem.defaultProps = {
    title: "",
    href: "",
    highlights: [],
    breadcrumbs: [],
    customMetadata: {
        taxonomy: {}
    }
}

const searchResultListStyles = createStyles((theme: any) => ({
    searchResultListPaginationWrapper: theme.searchResultListPaginationWrapper,
    searchResultListPagination: theme.searchResultListPagination,
    searchResultListContainer: theme.searchResultListContainer,
    searchResultListCount: theme.searchResultListCount,
    searchResultList: theme.searchResultList,
}));

export interface ISearchResultProps {
    resultsCount?: number;
    results?: any[];
    page?: number;
    pageSize?: number;
    paginationProps?: any;
    containerStyles?: any;
    resultsMetaStyles?: any;
    searchQuery?: any,
    classes?: any,

    onPageClick?(_evt, _offset, p): void;

    RowComponent: React.ReactNode
}

const SearchResultList = ({
                              classes,
                              resultsCount,
                              results,
                              page,
                              pageSize,
                              paginationProps,
                              onPageClick,
                              RowComponent,
                              searchQuery
                          }: ISearchResultProps) => {
    const maxPage = resultsCount > 0 ? Math.floor(resultsCount/(pageSize || 1)) : 0
    const actualPage = Math.min(page, maxPage)
    const startIndex = (actualPage * pageSize) + 1;
    const endIndex = Math.min((actualPage * pageSize) + pageSize, resultsCount);
    const offset = pageSize * actualPage;
    return (
        <div className={classes.searchResultList}>
            <div className={`${classes.searchResultListCount}`}>
                <I18nMessage id={'message.search.results-found'} values={{
                    count: resultsCount,
                    startIndex,
                    endIndex,
                    search: multiReplaceAmp(searchQuery.queryString),
                }}/>
            </div>
            <div className={`${classes.searchResultListContainer}`}>
                {results?.map((row, i) => (
                    // @ts-ignore
                    <RowComponent key={(row.title || "") + i}
                                  {...row}/>
                ))}
            </div>

            <div className={classes.searchResultListPaginationWrapper}>
                {
                    resultsCount > 0 &&
                    <Pagination
                    limit={pageSize}
                    offset={offset}
                    total={resultsCount}
                    // Normally we'd use CSS to have dynamic changes based on screen size, but this is a prop, not a class, and requires a re-render to update.
                    // At 480px wide, regardless of the number of pages, the pagination container begins to collapse weird at a large size.
                    // So, if pagination size is not specified, we default to using 480 to determine whether to render a large or small container.
                    size={paginationProps.size || (window.innerWidth > 480 ? "large" : "medium")}
                    classes={{
                        root: classes.searchResultListPagination,
                        ...paginationProps.classes,
                    }}
                    innerRef={paginationProps.innerRef}
                    onClick={onPageClick}
                    />
                }
            </div>
        </div>
    );
};

SearchResultList.defaultProps = {
    resultsCount: 0,
    results: [],
    page: 0,
    pageSize: 10,
    paginationProps: {},
    containerStyles: {},
    resultsMetaStyles: {},
    searchQuery: {},
    RowComponent: withStyles(searchResultItemStyles, { name: "SearchResultItem" })(SearchResultItem)
}

export default withStyles(searchResultListStyles, { name: "SearchResultList" })(SearchResultList);
